import axios from 'axios';
import {EventBus} from '@/plugins/eventBus';
import router from '@/router';
import * as types from '../mutation-types';
import {createUrlQueryString} from '@/utils/urls';
import {get} from 'lodash';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {PermissionsArray} from '@/types';
import type {CompanyInterface} from '@/types/company';
import type {Module} from 'vuex';
import type {RootState} from '@/types/RootState';
import {push} from 'notivue'

export interface SuperUserState {
    loading: Record<string, boolean>,
    users: Array<any>,
    companies: CompanyInterface[],
    companiesMeta: {
        current_page: number,
        from: number,
        last_page: number,
        per_page: number,
        to: number,
        total: number,
    },
    currentWorkingCompany: any,
    currentWorkingUser: any,
    roles: Array<any>,
    permissions: PermissionsArray,
    media: Array<any>,
    mediaDirectories: Array<any>,
    mediaPath: string,
    allListingImages: Array<any>,
    serverDetails: any,
    basicStats: {
        active_users: Array<any>,
        subscribers: any,
        users: number | null,
        trials: number | null,
        listings: number | null,
        automations: number | null,
        bookings: number | null,
        connections: {
            tesla_accounts: number | null,
            turo_accounts: number | null,
            getaround_accounts: number | null,
        },
    },
    featureWaitlist: Array<any>,
    featuresStatus: Array<any>,
    cache: Array<any>,
    allFeatures: Array<any>,
}

const state: SuperUserState = {
    loading: {
        users: false,
        roles: false,
        permissions: false,
        automations: false,
        bookings: false,
        connections: false,
        listings: false,
        subscribers: false,
        activeUsers: false,
        turoTrip: false,
        upload: false,
    },
    users: [],
    companies: [],
    companiesMeta: {
        current_page: 1,
        from: 1,
        last_page: 1,
        per_page: 1,
        to: 1,
        total: 1,
    },
    currentWorkingCompany: {},
    currentWorkingUser: {},
    roles: [],
    permissions: [],
    media: [],
    mediaDirectories: [],
    mediaPath: '',
    allListingImages: [],
    serverDetails: {},
    basicStats: {
        active_users: [],
        subscribers: {},
        users: null,
        trials: null,
        listings: null,
        automations: null,
        bookings: null,
        connections: {
            tesla_accounts: null,
            turo_accounts: null,
            getaround_accounts: null,
        },
    },

    featureWaitlist: [],
    featuresStatus: [],
    cache: [],
    allFeatures: [],
};

const getters = {
    loading: (state: SuperUserState) => state.loading,
    allUsers: (state: SuperUserState) => state.users,
    allCompanies: (state: SuperUserState) => state.companies,
    companiesMeta: (state: SuperUserState) => state.companiesMeta,
    currentWorkingUser: (state: SuperUserState) => state.currentWorkingUser,
    currentWorkingCompany: (state: SuperUserState) => state.currentWorkingCompany,
    roles: (state: SuperUserState) => state.roles,
    permissions: (state: SuperUserState) => state.permissions,
    mediaDirectories: (state: SuperUserState) => state.mediaDirectories,
    media: (state: SuperUserState) => state.media,
    mediaPath: (state: SuperUserState) => state.mediaPath,
    allListingImages: (state: SuperUserState) => state.allListingImages,
    basicStats: (state: SuperUserState) => state.basicStats,
    serverDetails: (state: SuperUserState) => state.serverDetails,
    featureWaitlist: (state: SuperUserState) => state.featureWaitlist,
    featuresStatus: (state: SuperUserState) => state.featuresStatus,
    allFeatures: (state: SuperUserState) => state.allFeatures,
    cache: (state: SuperUserState) => state.cache,
};

const mutations = {
    [types.SET_SU_LOADING](state: SuperUserState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_ALL_USERS](state: SuperUserState, users) {
        state.users = users;
    },
    [types.SET_ALL_COMPANIES](state: SuperUserState, companies) {
        if (companies.current_page === 1) {
            state.companies = companies.data;
        } else {
            // state.companies = state.companies.concat(companies.data);
        }

        state.companiesMeta = {
            current_page: companies.current_page,
            from: companies.from,
            last_page: companies.last_page,
            per_page: companies.per_page,
            to: companies.to,
            total: companies.total,
        };
    },
    [types.SET_CURRENT_WORKING_COMPANY](state: SuperUserState, company) {
        state.currentWorkingCompany = company;
    },
    [types.SET_CURRENT_WORKING_USER](state: SuperUserState, user) {
        state.currentWorkingUser = user;
    },
    [types.SET_ALL_ROLES](state: SuperUserState, roles) {
        state.roles = roles;
    },
    [types.SET_ALL_PERMISSIONS](state: SuperUserState, permissions) {
        state.permissions = permissions;
    },
    [types.SET_ALL_MEDIA_DIRECTORIES](state: SuperUserState, mediaDirectories) {
        state.mediaDirectories = mediaDirectories;
    },
    [types.SET_ALL_MEDIA_IN_DIRECTORY](state: SuperUserState, media) {
        state.media = media;
    },
    [types.SET_MEDIA_BASE_PATH](state: SuperUserState, path) {
        state.mediaPath = path;
    },
    [types.SET_ALL_LISTING_IMAGES](state: SuperUserState, images) {
        state.allListingImages = images;
    },
    [types.SET_ALL_SERVER_DETAILS](state: SuperUserState, details) {
        state.serverDetails = details;
    },
    [types.SET_SA_FEATURE_WAITLIST](state: SuperUserState, waitlist) {
        state.featureWaitlist = waitlist;
    },
    [types.SET_SA_FEATURES_STATUS](state: SuperUserState, status) {
        state.featuresStatus = status;
    },
    [types.SET_SU_ALL_FEATURES](state: SuperUserState, allFeatures) {
        state.allFeatures = allFeatures;
    },
    [types.SET_SA_CACHE](state: SuperUserState, cache) {
        state.cache = cache;
    },
    [types.SET_CURRENT_WORKING_SU_BASIC_STATS](state: SuperUserState, stats) {
        state.basicStats[Object.keys(stats)[0]] = stats[Object.keys(stats)[0]];
    },
};

const actions = {
    async fetchAllUsers({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get('/v1/su/users'));
        commit('SET_ALL_USERS', responseData.users);
    },

    async fetchOneUsers({commit}, uuid) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get(`/v1/su/users/${uuid}`));
        const user = responseData.user;
        user.features = responseData.features;
        commit('SET_CURRENT_WORKING_USER', user);
    },
    async purgeFeatures({commit}) {
        await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.post('/v1/su/features/purge'));
    },
    async getFeaturesWaitlist({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get('/v1/su/features-waitlist'));
        commit('SET_SA_FEATURE_WAITLIST', responseData.waitlist);
    },
    setAllowFeature(_ctx, feature) {
        axios.post('/v1/su/features/allow', feature)
            .then(() => {
                EventBus.$emit('sa-set-allow-feature-success');
            }).catch(() => {
        }).finally(() => {
        });
    },
    setRemoveFeature(_ctx, feature: string) {
        axios.post('/v1/su/features/remove', feature)
            .then(() => {
                EventBus.$emit('sa-set-allow-feature-success');
            }).catch(() => {
        }).finally(() => {
        });
    },
    setAllowFeatureFromWishlist(_ctx, feature: string) {
        axios.post(`/v1/su/features-waitlist/allow/${feature}`)
            .then(() => {
                // commit('SET_SA_FEATURE_WAITLIST', response.data.waitlist);
            }).catch(() => {
        }).finally(() => {
        });
    },

    getFeatures({commit}) {
        axios.get('/v1/su/features')
            .then((response) => {
                commit('SET_SA_FEATURES_STATUS', response.data.featuresStatus);
            }).catch(() => {
        }).finally(() => {
        });
    },

    async getAllFeatures({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'features', () => axios.get('/v1/su/all-features'));

        commit('SET_SU_ALL_FEATURES', responseData.features);

        return responseData;
    },

    async testPdfCertSign({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'features', () => axios.post('/v1/su/pdf-cert-test'));

        commit('SET_SU_ALL_FEATURES', responseData.features);

        return responseData;
    },

    getCache({commit}) {
        axios.get('/v1/su/cache')
            .then((response) => {
                commit('SET_SA_CACHE', response.data.cache);
            }).catch(() => {
        }).finally(() => {
        });
    },

    clearCache(_ctx, data = null) {
        axios.delete('/v1/su/cache', {data})
            .catch(() => {
            }).finally(() => {
        });
    },

    async getFleetwirePartners({commit}) {
        return await performApiRequest(commit, 'SET_SU_LOADING', 'getFleetwirePartners', () => axios.get('/v1/su/partners'));
    },

    getFleetwirePartner(_ctx, getFleetwirePartner: string) {
        axios.get(`/v1/su/partners/${getFleetwirePartner}`)
            .then((response) => {
                EventBus.$emit('get-fleetwire-partner-success', response.data);
            }).catch(() => {
        }).finally(() => {
        });
    },

    async updateUser({commit}, user) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.put(`/v1/su/users/${user.uuid}`, user));
        commit('SET_CURRENT_WORKING_USER', responseData.user);

        push.success({
            message: 'User Saved',
        });
    },

    fetchAllCompanies({commit}, data) {
        const page = get(data, 'page', 1) || 1;

        axios.get(`/v1/su/company?page=${page}`)
            .then((response) => {
                commit('SET_ALL_COMPANIES', response.data);
            }).catch(() => {
        }).finally(() => {
        });
    },

    async fetchAllMedia({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get('/v1/su/media'));
        commit(types.SET_ALL_MEDIA_DIRECTORIES, responseData.directories);
        return responseData;
    },
    async fetchDirectories({commit}, payload: { path: string }) {
        const urlParams = new URLSearchParams();
        urlParams.append('path', payload.path);
        const query = urlParams.toString();
        return await performApiRequest(commit, 'SET_SU_LOADING', 'directory', () => axios.get(`/v1/su/directory?${query}`));
    },

    async fetchServerDetails({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get('/v1/su/server-details'));
        commit(types.SET_ALL_SERVER_DETAILS, responseData.details);
    },

    async fetchAllMediaInDirectory({commit}, directory: string) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get(`/v1/su/media/directory?directory=${directory}`));
        commit(types.SET_ALL_MEDIA_IN_DIRECTORY, responseData.media);
        commit(types.SET_MEDIA_BASE_PATH, responseData.path);
        return responseData;
    },

    fetchAllListingImages({commit}) {
        axios.get('/v1/su/listing-images')
            .then((response) => {
                commit('SET_ALL_LISTING_IMAGES', response.data.images);
            }).catch(() => {
        }).finally(() => {
        });
    },

    resizeImage(_ctx, path) {
        axios.put('/v1/su/resize-image', {path})
            .then(() => {
            }).catch(() => {
        }).finally(() => {
        });
    },

    fetchOneCompany({commit}, uuid: string) {
        axios.get(`/v1/su/company/${uuid}`)
            .then((response) => {
                commit('SET_CURRENT_WORKING_COMPANY', response.data.company);
            }).catch(() => {
        }).finally(() => {
        });
    },

    updateCompany({commit, state}) {
        axios.put(`/v1/su/company/${state.currentWorkingCompany.uuid}`, state.currentWorkingCompany)
            .then((response) => {
                commit('SET_CURRENT_WORKING_COMPANY', response.data.company);

                push.success({
                    message: 'Company Updated',
                });
            }).catch(() => {
        }).finally(() => {
        });
    },

    deleteCompany({commit, state}) {
        axios.delete(`/v1/su/company/${state.currentWorkingCompany.uuid}`)
            .then((response) => {
                commit('SET_CURRENT_WORKING_USER', response.data.user);

                push.success({
                    message: 'Company Deleted',
                });
            }).catch(() => {
        }).finally(() => {
        });
    },

    getBasicStats({commit}) {
        axios.get('/v1/su/stats')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', response.data.stats);
            }).catch(() => {
        }).finally(() => {
        });
    },

    getActiveUsersStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'activeUsers', loading: true});

        axios.get('/v1/su/stats/users')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {active_users: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'activeUsers', loading: false});
        });
    },

    testUserAPN({commit}) {
        commit('SET_SU_LOADING', {subject: 'activeUsers', loading: true});

        axios.get('/v1/su/pusher-test-user-notification')
            .then(() => {
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'activeUsers', loading: false});
        });
    },

    getSubscribersStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'subscribers', loading: true});

        axios.get('/v1/su/stats/subscribers')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {subscribers: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'subscribers', loading: false});
        });
    },

    getListingStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'listings', loading: true});

        axios.get('/v1/su/stats/listingStats')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {listings: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'listings', loading: false});
        });
    },

    getConnectionsStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'connections', loading: true});

        axios.get('/v1/su/stats/connectionsStats')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {connections: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'connections', loading: false});
        });
    },

    getAutomations({commit}, data) {
        commit('SET_SU_LOADING', {subject: 'automations', loading: true});

        const query = createUrlQueryString(data);

        axios.get(`/v1/su/stats/automations${query}`)
            .then((response) => {
                EventBus.$emit('get-automations-success', response.data);
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'automations', loading: false});
        });
    },
    getAutomationsStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'automations', loading: true});

        axios.get('/v1/su/stats/automationsStats')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {automations: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'automations', loading: false});
        });
    },
    getAutomationsStatsData({commit}, data) {
        commit('SET_SU_LOADING', {subject: 'automations', loading: true});

        const query = createUrlQueryString(data);

        axios.get(`/v1/su/stats/automationsStatsData${query}`)
            .then((response) => {
                EventBus.$emit('get-automation-stats-data-success', response.data);
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'automations', loading: false});
        });
    },
    getTuroTrip({commit}, data) {
        commit('SET_SU_LOADING', {subject: 'turoTrip', loading: true});

        const query = createUrlQueryString(data);

        axios.get(`/v1/su/stats/getTuroTrip${query}`)
            .then((response) => {
                EventBus.$emit('get-turo-trip-success', response.data);
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'turoTrip', loading: false});
        });
    },
    getBookingsStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'bookings', loading: true});

        axios.get('/v1/su/stats/bookingStats')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {bookings: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'bookings', loading: false});
        });
    },

    async fetchAllRoles({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'roles', () => axios.get('/v1/roles'));
        commit('SET_ALL_ROLES', responseData.roles);
    },

    async fetchAllPermissions({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'permissions', () => axios.get('/v1/permissions'));
        commit('SET_ALL_PERMISSIONS', responseData.permissions);
    },

    createPermission({commit}, data) {
        axios.post('/v1/permissions', data)
            .then(async (response) => {
                commit('SET_ALL_PERMISSIONS', response.data.permissions);

                push.success({
                    message: 'Permission Created',
                });

                await router.push({name: 'super.admin.permissions'});
            }).catch(() => {
        }).finally(() => {
        });
    },

    updatePermission({commit}, data) {
        axios.put(`/v1/permissions/${data.id}`, data)
            .then((response) => {
                commit('SET_ALL_PERMISSIONS', response.data.permissions);
                push.success({
                    message: 'Permission Updated',
                });
            }).catch(() => {
        }).finally(() => {
        });
    },

    deletePermission({commit}, data) {
        axios.delete(`/v1/permissions/${data.id}`)
            .then(async (response) => {
                commit('SET_ALL_PERMISSIONS', response.data.permissions);
                push.success({
                    message: 'Permission Deleted',
                });
                await router.push({name: 'super.admin.permissions'});
            }).catch(() => {
        }).finally(() => {
        });
    },

    masqueradeInit({commit, rootGetters}, user) {
        axios.get(`/v1/users/${user.uuid}/masquerade`)
            .then((response) => {
                commit('app/SET_MASQUERADE', {
                    user: response.data.user,
                    token: response.data.token,
                    admin: {
                        email: rootGetters['auth/user'].email,
                        uuid: rootGetters['auth/user'].uuid,
                    },
                }, {root: true});
            }).catch(() => {
        }).finally(() => {
        });
    },

    encryptText(_ctx, text: string) {
        axios.post('/v1/su/encrypt-text', {text})
            .then((response) => {
                EventBus.$emit('su-encrypt-text-success', response.data.result);
            }).catch((e) => {
            EventBus.$emit('su-encrypt-text-failed', e.response.data.message);
        }).finally(() => {
        });
    },

    decryptText(_ctx, text: string) {
        axios.post('/v1/su/decrypt-text', {text})
            .then((response) => {
                EventBus.$emit('su-decrypt-text-success', response.data.result);
            }).catch((e) => {
            EventBus.$emit('su-decrypt-text-failed', e.response.data.message);
        }).finally(() => {
        });
    },

    async uploadMedia({commit}, formData: FormData) {
        return await performApiRequest(commit, 'SET_SU_LOADING', 'upload', () => axios.post('/v1/su/upload-media', formData));
    },
};

const superUserModule: Module<SuperUserState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};

export default superUserModule;
